import { useToastProvider } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { changeControlApi } from '../api/changeControl';
import {
  CCWorkflowLink,
  ChangeControlTemplateDTO,
} from '../types/DocumentCreate';

export const useCCWorkflowData = (
  companyId: number,
  templateId: number | null,
  enabled: boolean,
): {
  allCCTemplates: ChangeControlTemplateDTO[];
  linkedCCWorkflows: CCWorkflowLink[] | undefined;
  isLoadingLinkedWorkflows: boolean;
  isLoadingAllCCTemplates: boolean;
} => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const { data: rawCCTemplates = [], isLoading: isLoadingAllCCTemplates } =
    useQuery<ChangeControlTemplateDTO[]>({
      queryKey: ['changeControl'],
      queryFn: async () => {
        const templates = await changeControlApi.fetchChangeControlTemplates();
        return templates ?? [];
      },
      enabled,
      refetchOnWindowFocus: false,
    });

  const { data: rawLinkedWorkflows, isLoading: isLoadingLinkedWorkflows } =
    useQuery<CCWorkflowLink[]>({
      queryKey: ['linkedCCWorkflows', templateId],
      queryFn: () =>
        changeControlApi.fetchLinkedCCWorkflows(
          companyId,
          templateId as number,
        ),
      enabled: enabled && Boolean(templateId),
      refetchOnWindowFocus: false,
      onError: () => {
        showToast({
          'data-cy': 'cc-workflow-fetch-error-toast',
          id: 'fetch-linked-cc-workflows-error',
          status: 'error',
          title: t('notifications.error'),
          description: t('newDocument.failedToLoadLinkedCCWorkflows'),
          replace: true,
        });
      },
    });

  const allCCTemplates = rawCCTemplates.filter((t) => t.status !== 'archived');
  const linkedCCWorkflows = rawLinkedWorkflows?.filter(
    (wf) => wf.status !== 'archived',
  );

  return {
    allCCTemplates,
    linkedCCWorkflows,
    isLoadingLinkedWorkflows,
    isLoadingAllCCTemplates,
  };
};
